import React from "react";
import { useSearchParams } from "react-router-dom";
import GenericTabs from "@ui-components/Tabs/GenericTabs";
import GenericCard from "@ui-components/Card/GenericCard";
import InternetIpMonitoringPage from "../InternetIpMonitoring/InternetIpMonitoringPage";
import SiteLatencyMatrix from "../SiteLatencyMatrix/SiteLatencyMatrix";

type NetworkMonitoringTab = "internet" | "latency";

const TABS: { label: string; value: NetworkMonitoringTab }[] = [
    { label: "Internet", value: "internet" },
    { label: "Latency", value: "latency" },
];

function isNetworkMonitoringTab(
    value: string | null
): value is NetworkMonitoringTab {
    return TABS.some((tab) => tab.value === value);
}

/**
 * Network monitoring (CTX-842): the partner's view of the backbone. The
 * Internet tab is the IPSLA view that used to be the whole page; Latency is
 * the round-trip time between sites. Laid out like Alerts — one card, the
 * tabs in a bar across its top — and the tab lives in `?tab=` so a link can
 * land on either one.
 */
const NetworkMonitoringPage: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const requested = searchParams.get("tab");
    const activeTab: NetworkMonitoringTab = isNetworkMonitoringTab(requested)
        ? requested
        : "internet";

    const handleTabChange = (value: string) => {
        if (!isNetworkMonitoringTab(value)) return;
        setSearchParams({ tab: value }, { replace: true });
    };

    return (
        <GenericCard isCollapsible={false} className="flex flex-col overflow-hidden">
            <div className="flex shrink-0 items-center border-b border-semantic-border-primary px-4 py-3">
                <GenericTabs
                    tabs={TABS}
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    fullWidth={false}
                    fitContent
                    size="sm"
                    aria-label="Network monitoring view"
                />
            </div>

            {activeTab === "internet" ? (
                <InternetIpMonitoringPage />
            ) : (
                <div className="p-4">
                    <SiteLatencyMatrix />
                </div>
            )}
        </GenericCard>
    );
};

export default NetworkMonitoringPage;
